import React, { useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { format, parseISO } from 'date-fns';
import { enUS, ja } from 'date-fns/locale';
import { CalendarDaysIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'react-i18next';

import { toBusinessDate } from 'config/time';

type DateFilterProps = {
  defaultDate?: string;
  onDateChange: (value: string) => void;
};

const DateFilter: React.FC<DateFilterProps> = ({
  defaultDate,
  onDateChange,
}) => {
  const { t, i18n } = useTranslation('common');
  // EV-116。カレンダーは暦日を「端末の0時」の Date で扱う。初期値（YYYY-MM-DD）も端末の0時として読む。
  // new Date('YYYY-MM-DD') は UTC の0時になり、日本より西の端末では前の日が選ばれて見えていた。
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    defaultDate ? parseISO(defaultDate) : null
  );
  // ADM-05。UTC日付へ丸めると、日本時間の朝が前日として扱われる。
  const today = toBusinessDate(new Date());

  // EV-116。選んだ暦日は、カレンダーと同じ端末の時間帯で取り出す。日本時間へ変換すると、
  // 日本より東の端末（例 Pacific/Auckland）では前の日になっていた。
  const pickedDate = (date: Date) => format(date, 'yyyy-MM-dd');

  const handleDateChange = (date: Date | null) => {
    setSelectedDate(date);
    onDateChange(date ? pickedDate(date) : '');
  };

  const formatDate = (date: Date | null) => {
    if (!date) return '';
    const formattedDate = pickedDate(date);
    return formattedDate === today
      ? t('date-filter.today')
      : format(date, t('date-filter.format'), {
          locale: i18n.language === 'jp' ? ja : enUS,
        });
  };

  return (
    <div className="inline-flex h-10 items-center p-4 pt-5 border rounded-md cursor-pointer max-w-fit">
      <DatePicker
        selected={selectedDate}
        onChange={handleDateChange}
        dateFormat={t('date-filter.format')}
        locale={i18n.language === 'jp' ? ja : enUS}
        customInput={
          <div className="flex gap-2 items-center">
            <CalendarDaysIcon className="w-5 h-5 text-primary/300" />
            <input
              type="text"
              value={formatDate(selectedDate)}
              readOnly
              // 2026-10-22 axe（label）。値は「今日」などの日付だけなので、何の欄かを名前で伝える。
              aria-label={t('date-filter.label')}
              className="w-36 focus:outline-none appearance-none cursor-pointer text-center"
            />
          </div>
        }
      />
    </div>
  );
};

export default DateFilter;
